import { omit, omitUndefined } from '@shared/helpers/object.helpers';
import { designGuards } from '../design-typeguards.helpers';
import { v4 } from 'uuid';
import {
  Shape,
  FactoryShape,
  DraftShape,
} from '@shared/models/design/storey.model';
import { isPolygon2D } from '@shared/helpers/geometry/geometry.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

/**
 * Create a new shape
 * @param base overrides on default
 * @returns
 */
export const createShape = (base: FactoryShape | DraftShape): Shape => {
  if (designGuards.is.draftShape(base)) {
    return createShape(omit(base, 'draft'));
  }

  // Support both FactoryShape and Point2D[]
  const { points, ...rest } = guards.is.array(base)
    ? { points: base }
    : omitUndefined(base);

  if (!isPolygon2D(points)) {
    throw new Error('Points must be an array with at least 3 points');
  }

  // Reuse id if already provided
  const id = rest.id ?? v4();

  return {
    ...rest,
    points,
    id,
  };
};

/**
 * Create multiple shapes. Note, will regenerate ids for all shapes.
 * @param factoryShapes
 * @returns
 */
export const createShapes = (factoryShapes: FactoryShape[] = []): Shape[] => {
  return factoryShapes.map((s) =>
    createShape(guards.is.array(s) ? s : omit(s, 'id')),
  );
};

/**
 * Create a shape that is not yet finished and can have less than 3 points)
 * @param base Base shape to create the draft shape from
 * @returns
 */
export const createDraftShape = (
  base: Partial<DraftShape> = {},
): DraftShape => {
  return {
    points: [],
    id: v4(),
    ...omitUndefined(base),
    draft: true, // Must always be true
  };
};
